import { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Smartphone, Wallet, CheckCircle2, Moon } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal } from "../components/motion";
import { useCart } from "../context/CartContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState({ email: "", name: "", address: "", city: "", zip: "", country: "US" });
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);

  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 6.95;
  const total = subtotal + shipping;

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { ...form, items });
      setOrder(data);
      clear();
      toast.success("Order confirmed! Sweet dreams ahead.");
    } catch {
      toast.error("Couldn't place the order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (order) return (
    <div className="bg-cream" data-testid="order-confirmation">
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blush">
          <CheckCircle2 className="text-pinkdeep" size={40} />
        </span>
        <h1 className="mt-6 font-display text-4xl font-medium text-navy">Sweet dreams are on the way.</h1>
        <p className="mt-4 text-navy/60">Order <span className="font-mono font-bold text-pinkdeep">#{order.id.slice(-8).toUpperCase()}</span> is confirmed. A confirmation is on its way to your inbox.</p>
        <p className="mt-2 text-sm text-navy/40">(Demo checkout — no payment was charged.)</p>
        <Link to="/buddy/luna-the-unicorn" data-testid="order-continue-btn"
          className="mt-8 inline-block rounded-full bg-pink px-8 py-4 font-display font-semibold text-navy transition hover:bg-moon">
          MEET MORE BUDDIES
        </Link>
      </div>
    </div>
  );

  if (items.length === 0) return (
    <div className="bg-cream" data-testid="checkout-empty">
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <Moon className="mx-auto text-lavdeep" size={44} />
        <h1 className="mt-6 font-display text-3xl font-medium text-navy">Your cart is empty.</h1>
        <Link to="/buddy/luna-the-unicorn" data-testid="checkout-empty-cta" className="mt-8 inline-block rounded-full bg-pink px-8 py-4 font-display font-semibold text-navy">FIND THEIR BUDDY</Link>
      </div>
    </div>
  );

  const inputCls = "w-full rounded-2xl border border-blush bg-cream px-5 py-3.5 text-sm text-navy placeholder:text-navy/35 focus:border-pink focus:outline-none";

  return (
    <div className="bg-cream" data-testid="checkout-page">
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-14 lg:grid-cols-5 lg:py-20">
        <Reveal className="lg:col-span-3">
          <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Almost bedtime.</h1>
          <p className="mt-2 text-sm text-navy/45">Demo checkout — no real payment is processed.</p>
          <form onSubmit={placeOrder} className="mt-8 space-y-4" data-testid="checkout-form">
            <h2 className="font-display text-lg font-semibold text-pinkdeep">Contact</h2>
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              aria-label="Email" data-testid="checkout-email" className={inputCls} />
            <h2 className="pt-3 font-display text-lg font-semibold text-pinkdeep">Shipping</h2>
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              aria-label="Full name" data-testid="checkout-name" className={inputCls} />
            <input required placeholder="Street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              aria-label="Street address" data-testid="checkout-address" className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                aria-label="City" data-testid="checkout-city" className={inputCls} />
              <input required placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })}
                aria-label="ZIP code" data-testid="checkout-zip" className={inputCls} />
            </div>
            <h2 className="pt-3 font-display text-lg font-semibold text-pinkdeep">Payment</h2>
            <div className="grid grid-cols-3 gap-3">
              {[[CreditCard, "Card"], [Smartphone, "Apple Pay"], [Wallet, "Google Pay"]].map(([Icon, label], i) => (
                <button type="button" key={label} data-testid={`pay-${label.toLowerCase().replace(/ /g, "-")}`}
                  className={`flex flex-col items-center gap-2 rounded-2xl border py-4 text-xs font-bold transition ${i === 0 ? "border-pink bg-blush text-navy" : "border-blush text-navy/45 hover:border-pink"}`}>
                  <Icon size={20} /> {label}
                </button>
              ))}
            </div>
            <input placeholder="Card number (demo)" aria-label="Card number" data-testid="checkout-card" className={inputCls} />
            <button type="submit" disabled={placing} data-testid="place-order-btn"
              className="w-full rounded-full bg-pink py-4 font-display text-base font-semibold tracking-wide text-navy transition hover:scale-[1.02] hover:bg-moon disabled:opacity-50">
              {placing ? "Tucking in your order…" : `PLACE ORDER — $${total.toFixed(2)}`}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="rounded-[2rem] bg-white p-7 shadow-[0_16px_50px_rgba(16,42,86,0.08)]" data-testid="order-summary">
            <h2 className="font-display text-xl font-semibold text-navy">Order summary</h2>
            <ul className="mt-5 space-y-4">
              {items.map((i) => (
                <li key={i.slug} className="flex items-center gap-4">
                  <img src={i.image} alt={i.name} className="h-16 w-14 rounded-2xl object-cover" />
                  <div className="flex-1">
                    <p className="font-display text-sm font-semibold text-navy">{i.name}</p>
                    <p className="text-xs text-navy/45">Qty {i.qty}</p>
                  </div>
                  <span className="text-sm font-bold text-pinkdeep">${(i.price * i.qty).toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-2 border-t border-blush pt-5 text-sm">
              <div className="flex justify-between text-navy/55"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-navy/55"><span>Shipping</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between pt-2 font-display text-lg font-semibold text-navy">
                <span>Total</span><span data-testid="order-total">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
